import React, { useState } from 'react';
import { Form, Button, Input, Segment, Message, Tab, Label, Divider } from 'semantic-ui-react';
import { auth, db } from '../../../firebase/config';
import { collection, addDoc, doc, getDoc } from 'firebase/firestore';

interface CloseBookQuestion {
  id: string;
  text: string;
  points: number;
}

interface CloseBookTestProps {
  moduleId: string;
  onTestCreated: () => void;
}

const CloseBookTest: React.FC<CloseBookTestProps> = ({ moduleId, onTestCreated }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [testName, setTestName] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  // Close Book Test state
  const [shortAnswerTotal, setShortAnswerTotal] = useState(12);
  const [shortAnswerRequired, setShortAnswerRequired] = useState(10);
  const [shortAnswerPoints, setShortAnswerPoints] = useState(3);
  const [shortAnswerQuestions, setShortAnswerQuestions] = useState<CloseBookQuestion[]>(
    Array.from({ length: 12 }, (_, i) => ({
      id: `sa-${i + 1}`,
      text: '',
      points: 3
    }))
  );

  const [longAnswerTotal, setLongAnswerTotal] = useState(6);
  const [longAnswerRequired, setLongAnswerRequired] = useState(5);
  const [longAnswerPoints, setLongAnswerPoints] = useState(5);
  const [longAnswerQuestions, setLongAnswerQuestions] = useState<CloseBookQuestion[]>(
    Array.from({ length: 6 }, (_, i) => ({
      id: `la-${i + 1}`,
      text: '',
      points: 5
    }))
  );

  // Verse Memorization state
  const [verseTotalGrade, setVerseTotalGrade] = useState<number>(0);

  const totalGrade = shortAnswerRequired * shortAnswerPoints + longAnswerRequired * longAnswerPoints;

  const updateShortAnswerCount = (total: number) => {
    setShortAnswerTotal(total);
    const current = shortAnswerQuestions;
    if (total > current.length) {
      const newQuestions = [...current];
      for (let i = current.length; i < total; i++) {
        newQuestions.push({
          id: `sa-${i + 1}`,
          text: '',
          points: shortAnswerPoints
        });
      }
      setShortAnswerQuestions(newQuestions);
    } else if (total < current.length) {
      setShortAnswerQuestions(current.slice(0, total));
    }
  };

  const updateLongAnswerCount = (total: number) => {
    setLongAnswerTotal(total);
    const current = longAnswerQuestions;
    if (total > current.length) {
      const newQuestions = [...current];
      for (let i = current.length; i < total; i++) {
        newQuestions.push({
          id: `la-${i + 1}`,
          text: '',
          points: longAnswerPoints
        });
      }
      setLongAnswerQuestions(newQuestions);
    } else if (total < current.length) {
      setLongAnswerQuestions(current.slice(0, total));
    }
  };

  const handleShortQuestionChange = (index: number, text: string) => {
    const updated = [...shortAnswerQuestions];
    updated[index] = { ...updated[index], text };
    setShortAnswerQuestions(updated);
  };

  const handleLongQuestionChange = (index: number, text: string) => {
    const updated = [...longAnswerQuestions];
    updated[index] = { ...updated[index], text };
    setLongAnswerQuestions(updated);
  };

  const handleCloseBookSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const shortValid = shortAnswerQuestions.slice(0, shortAnswerTotal).every(q => q.text.trim());
      const longValid = longAnswerQuestions.slice(0, longAnswerTotal).every(q => q.text.trim());
      if (!shortValid || !longValid) {
        setError('Please fill in all question texts.');
        return;
      }
      if (shortAnswerRequired > shortAnswerTotal || longAnswerRequired > longAnswerTotal) {
        setError('Required answers cannot exceed total questions.');
        return;
      }

      const userDoc = await getDoc(doc(db, 'users', auth.currentUser!.uid));
      const userData = userDoc.data();
      const createdBy = userData?.initiatedName || `${userData?.firstName} ${userData?.lastName}`;

      const shortQuestions = shortAnswerQuestions.slice(0, shortAnswerTotal).map(q => ({
        ...q,
        points: shortAnswerPoints
      }));
      const longQuestions = longAnswerQuestions.slice(0, longAnswerTotal).map(q => ({
        ...q,
        points: longAnswerPoints
      }));

      const testData = {
        moduleId,
        testName,
        type: 'closeBook',
        questions: {
          shortAnswer: shortQuestions,
          longAnswer: longQuestions
        },
        config: {
          shortAnswer: {
            totalQuestions: shortAnswerTotal,
            requiredAnswers: shortAnswerRequired,
            pointsPerQuestion: shortAnswerPoints
          },
          longAnswer: {
            totalQuestions: longAnswerTotal,
            requiredAnswers: longAnswerRequired,
            pointsPerQuestion: longAnswerPoints
          }
        },
        totalGrade,
        examStatus: 'notStarted',
        createdAt: new Date(),
        createdBy
      };

      const moduleRef = doc(db, 'modules', moduleId);
      const testsCollectionRef = collection(moduleRef, 'tests');
      await addDoc(testsCollectionRef, testData);

      setSuccessMessage('Close Book Test created successfully!');
      onTestCreated();
    } catch (err) {
      console.error('Error creating test:', err);
      setError('Failed to create test. Please try again.');
    }
  };

  const handleVerseMemorizationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser!.uid));
      const userData = userDoc.data();
      const createdBy = userData?.initiatedName || `${userData?.firstName} ${userData?.lastName}`; 

      const testData = {
        moduleId,
        testName,
        type: 'verseMemorization',
        totalGrade: verseTotalGrade,
        examStatus: 'notStarted',
        createdAt: new Date(),
        createdBy
      };

      const moduleRef = doc(db, 'modules', moduleId);
      const testsCollectionRef = collection(moduleRef, 'tests');
      await addDoc(testsCollectionRef, testData);

      setSuccessMessage('Verse Memorization Test created successfully!');
      onTestCreated();
    } catch (err) {
      console.error('Error creating test:', err);
      setError('Failed to create test. Please try again.');
    }
  };

  const panes = [
    {
      menuItem: 'Close Book Test',
      render: () => (
        <Tab.Pane>
          <Form onSubmit={handleCloseBookSubmit}>
            {successMessage && <Message positive>{successMessage}</Message>}
            {error && <Message negative>{error}</Message>}

            <Form.Field>
              <label>Test Name</label>
              <Input
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
                placeholder="Enter test name"
                required
              />
            </Form.Field>

            <Segment>
              <Label attached="top">Short Answer Questions</Label>
              <Form.Group widths="equal">
                <Form.Field>
                  <label>Total Questions</label>
                  <Input
                    type="number"
                    value={shortAnswerTotal}
                    onChange={(e) => updateShortAnswerCount(Number(e.target.value))}
                    min={1}
                    max={20}
                  />
                </Form.Field>
                <Form.Field>
                  <label>Required Answers</label>
                  <Input
                    type="number"
                    value={shortAnswerRequired}
                    onChange={(e) => setShortAnswerRequired(Number(e.target.value))}
                    min={1}
                    max={shortAnswerTotal}
                  />
                </Form.Field>
                <Form.Field>
                  <label>Points Per Question</label>
                  <Input
                    type="number"
                    value={shortAnswerPoints}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      setShortAnswerPoints(val);
                      setShortAnswerQuestions(prev =>
                        prev.map(q => ({ ...q, points: val }))
                      );
                    }}
                    min={1}
                  />
                </Form.Field>
              </Form.Group>
              {shortAnswerQuestions.slice(0, shortAnswerTotal).map((q, idx) => (
                <Form.Field key={q.id}>
                  <label>Question {idx + 1}</label>
                  <Input
                    value={q.text}
                    onChange={(e) => handleShortQuestionChange(idx, e.target.value)}
                    placeholder={`Short answer question ${idx + 1}`}
                    required
                  />
                </Form.Field>
              ))}
            </Segment>

            <Segment>
              <Label attached="top">Long Answer Questions</Label>
              <Form.Group widths="equal">
                <Form.Field>
                  <label>Total Questions</label>
                  <Input
                    type="number"
                    value={longAnswerTotal}
                    onChange={(e) => updateLongAnswerCount(Number(e.target.value))}
                    min={1}
                    max={20}
                  />
                </Form.Field>
                <Form.Field>
                  <label>Required Answers</label>
                  <Input
                    type="number"
                    value={longAnswerRequired}
                    onChange={(e) => setLongAnswerRequired(Number(e.target.value))}
                    min={1}
                    max={longAnswerTotal}
                  />
                </Form.Field>
                <Form.Field>
                  <label>Points Per Question</label>
                  <Input
                    type="number"
                    value={longAnswerPoints}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      setLongAnswerPoints(val);
                      setLongAnswerQuestions(prev =>
                        prev.map(q => ({ ...q, points: val }))
                      );
                    }}
                    min={1}
                  />
                </Form.Field>
              </Form.Group>
              {longAnswerQuestions.slice(0, longAnswerTotal).map((q, idx) => (
                <Form.Field key={q.id}>
                  <label>Question {idx + 1}</label>
                  <Input
                    value={q.text}
                    onChange={(e) => handleLongQuestionChange(idx, e.target.value)}
                    placeholder={`Long answer question ${idx + 1}`}
                    required
                  />
                </Form.Field>
              ))}
            </Segment>

            <Label size="large">Total Grade: {totalGrade} points</Label>
            <Divider />
            <Button type="submit" primary>
              Create Close Book Test
            </Button>
          </Form>
        </Tab.Pane>
      )
    },
    {
      menuItem: 'Verse Memorization',
      render: () => (
        <Tab.Pane>
          <Form onSubmit={handleVerseMemorizationSubmit}>
            {successMessage && <Message positive>{successMessage}</Message>}
            {error && <Message negative>{error}</Message>}

            <Form.Field>
              <label>Test Name</label>
              <Input
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
                placeholder="Enter test name"
                required
              />
            </Form.Field>

            <Form.Field>
              <label>Total Grade</label>
              <Input
                type="number"
                value={verseTotalGrade}
                onChange={(e) => setVerseTotalGrade(Number(e.target.value))}
                min={0}
                required
              />
            </Form.Field>

            <Button type="submit" primary>
              Create Verse Memorization Test
            </Button>
          </Form>
        </Tab.Pane>
      )
    }
  ];

  return (
    <div>
      <Tab
        panes={panes}
        activeIndex={activeTab}
        onTabChange={(_, data) => {
          setActiveTab(data.activeIndex as number);
          setTestName('');
          setError('');
          setSuccessMessage('');
        }}
      />
    </div>
  );
};

export default CloseBookTest;
